/* eslint-disable no-param-reassign */
import { db } from '@/shared/services/firebase'
// import axiosIns from '@/libs/axios'

// ================ Get DB items ==================
const buildQueryConditions = (query, conditions) => {
  if (!conditions) {
    return query
  }

  conditions.forEach(condition => {
    query = query.where(condition.key, condition.operation, condition.value)
  })

  return query
}

const buildQuerySortOptions = (query, sortOptions) => {
  if (!sortOptions) {
    return query
  }

  if (sortOptions.orderBy) {
    query = query.orderBy(sortOptions.orderBy.key, sortOptions.orderBy.value)
  }

  return query
}

const buildQueryPagination = (query, pagination) => {
  if (!pagination) {
    return query
  }

  if (pagination.action === 'next') {
    query = query.startAfter(new Date(+pagination.timeStamp))
  } else if (pagination.action === 'prev') {
    query = query.endBefore(new Date(+pagination.timeStamp))
  }

  return query
}


const buildQueryLimit = (query, pagination) => {
  if (!pagination?.limit) {
    return query
  }

  if (pagination.action === 'prev') {
    query = query.limitToLast(pagination.limit)
  } else {
    query = query.limit(pagination.limit)
  }

  return query
}

const buildQueryGetMultipleItems = (dbName, conditions, sortOptions, pagination) => {
  let query = db.collection(dbName)

  query = buildQueryConditions(query, conditions)
  query = buildQuerySortOptions(query, sortOptions)
  query = buildQueryLimit(query, pagination)
  query = buildQueryPagination(query, pagination)

  return query
}

const getItemsFromDB = async (dbName, conditions, sortOptions, pagination, limit) => {
  let query = buildQueryGetMultipleItems(dbName, conditions, sortOptions, pagination)
  if (limit) {
    query = query.limit(limit)
  }
  const snapshot = await query.get()

  return snapshot.docs.map(doc => ({...doc.data(), id: doc.id}))
}

/**
 * fetchItems
 * @param {string} dbName required!
 * @param {array} conditions {key, operation, value}
 * @param {object} sortOptions {orderBy: {key, value}}
 * @param {object} pagination {action, timeStamp, limit}
 * @return {object} db item object
 */
export const fetchItems = async ( dbName, conditions = null, sortOptions = null, pagination = null, limit = null ) => {
  try {
    if (!dbName) {
      throw new Error( 'dbName is required')
    }

    const items = await getItemsFromDB(dbName, conditions, sortOptions, pagination, limit)

    return items
  } catch (error) {
    throw new Error(error)
  }
}
// ==========================================

// =================== Get single DB item ===============
/**
 * fetchSingleItem
 * @param {string} dbName required!
 * @param {string} docID required!
 * @return {promise} db item object
*/
export const fetchItem = async (dbName, docID) => {
  try {
    if (!dbName || !docID) {
      throw new Error( 'dbName and docID are required')
    }

    const doc = await db.collection(dbName).doc(docID).get()

    if (!doc.exists) {
      throw new Error (`Could not find document id: ${docID}`)
    }

    return {...doc.data(), id: doc.id}
  } catch (error) {
    throw new Error(error)
  }
}
// ===========================================================

// ===================== Delete DB item =====================
/**
 * deleteItem
 * @param {string} dbName required!
 * @param {string} docID required!
 * @return {promise}
*/
export const deleteItem = async (dbName, docID) => {
  try {
    if (!dbName || !docID) {
      throw new Error( 'dbName and docID are required')
    }

    await db.collection(dbName).doc(docID).update({
      status: 'deleted',
    })
  } catch (error) {
    throw new Error(error)
  }
}
// ===========================================================

// ===================== Partial update DB item ==============
/**
 * partialUpdateItem
 * @param {string} dbName required
 * @param {string} docID required
 * @param {object} data
 * @return {promise}
 */
export const partialUpdateItem = async (dbName, docID, data) => {
  try {
    if (!dbName || !docID || !data) {
      throw new Error("No dbName, docId or data")
    }
    await db.collection(dbName).doc(docID).update(data)
  } catch (error) {
    throw new Error("Error updating the document")
  }
}
// =============================================================

// ==================== Create DB item ========================
/**
 * createItem
 * @param {string} dbName required
 * @param {object} data required
 * @return {promise} Firestore response object
 */
export const createItem = async (dbName, data) => {
  try {
    if (!dbName || !data) {
      throw new Error("No dbName or data")
    }
    return await db.collection(dbName).add({...data})
  } catch (error) {
    throw new Error("Error updating the document")
  }
}

export const updateItem = async (dbName, data) => {
  try {
    if (!dbName || !data) {
      throw new Error("No dbName or data")
    }
    return await db.collection(dbName).doc(data.gigId).update({
      availableShifts: data.availableShifts,
    })
  } catch (error) {
    throw new Error("Error updating the document")
  } 
}
// =============================================================

// ========================= Util funtions to build params for query ====================
export const buildEqualCondition = (key, value) => ({key, operation: '==', value})

export const buildGreaterOrEqualCondition = (key, value,) => ({key, operation: '>=', value})

export const buildSmallerOrEqualCondition = (key, value) => ({key, operation: '<=', value})

export const buildInCondition = (key, value) => ({key, operation: 'in', value})

export const buildSortOption = (key, value) => ({orderBy: {key, value}})

export const buildLimit = limit => ({limit})